import { useEffect } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import { useDropdown } from '../hooks/useDropdown';
import {
	StyledArrow,
	StyledContainer,
	StyledDropdown,
	StyledDropdownItem,
	StyledDropdownList,
	StyledInputWrapper,
	StyledSearchInput,
	StyledSearchWrapper,
} from './Dropdown.styled';

interface RewardTypeOption {
	value: string;
	label: string;
}

interface RewardTypeSelectProps {
	options: RewardTypeOption[];
	value: string;
	onChange: (value: string) => void;
}

export const RewardTypeSelect: React.FC<RewardTypeSelectProps> = ({
	options,
	value,
	onChange,
}) => {
	const {
		isOpen,
		filterText,
		filteredOptions,
		containerRef,
		inputRef,
		listRef,
		selectedOption,
		handleToggle,
		handleSelect,
		handleInputChange,
		handleInputKeyDown,
	} = useDropdown(options, value, onChange);

	// Add sound to handleSelect
	const handleSelectWithSound = (option: RewardTypeOption) => {
		playSound(Sound.LinkCursor);
		handleSelect(option);
	};

	const handleToggleWithSound = () => {
		playSound(Sound.LinkCursor);
		handleToggle();
	};

	// Auto-scroll to selected item when dropdown opens
	useEffect(() => {
		if (isOpen && value && listRef?.current) {
			const selectedElement = listRef.current.querySelector(
				`[data-selected="true"]`,
			) as HTMLElement;
			if (selectedElement) {
				selectedElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
			}
		}
	}, [isOpen, value, filteredOptions]);

	return (
		<StyledContainer ref={containerRef}>
			<StyledInputWrapper $focus={isOpen} onClick={handleToggleWithSound}>
				<Text
					style={{
						flex: 1,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{selectedOption?.label || value || 'Выберите...'}
				</Text>
				<StyledArrow $active={isOpen}>▼</StyledArrow>
			</StyledInputWrapper>
			{isOpen && (
				<StyledDropdown>
					<StyledDropdownList ref={listRef}>
						{filteredOptions.length === 0 ? (
							<StyledDropdownItem disabled>
								<Text style={{ opacity: 0.5 }}>Ничего не найдено</Text>
							</StyledDropdownItem>
						) : (
							filteredOptions.map((option) => (
								<StyledDropdownItem
									key={option.value}
									$selected={option.value === value}
									data-selected={option.value === value}
									onClick={(e) => {
										e.stopPropagation();
										handleSelectWithSound(option);
									}}
									onMouseEnter={() => playSound(Sound.LinkCursor)}
								>
									<Text>{option.label}</Text>
								</StyledDropdownItem>
							))
						)}
					</StyledDropdownList>
					<StyledSearchWrapper>
						<StyledSearchInput
							ref={inputRef}
							value={filterText}
							onChange={handleInputChange}
							onKeyDown={handleInputKeyDown}
							placeholder="Поиск..."
							autoFocus
						/>
					</StyledSearchWrapper>
				</StyledDropdown>
			)}
		</StyledContainer>
	);
};
